import React, { useMemo } from 'react'
import { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import {
  useHideModal,
  useIsShowModal,
  useModalState,
} from 'src/Modal/ModalStateContext'
import { useAccountReceptors } from 'src/components/async/model/receptor/useReceptors'
import { useScanReceptor } from 'src/pages/Receptors/receptors.hooks'
import { setGrcWizardReceptorModelId } from 'src/components/GrcImportWizard/GrcImportWizard.store'
import {
  GRC_RECEPTOR_DISPLAY_NAMES,
  GRC_RECEPTOR_MODEL_IDS,
} from 'src/pages/Frameworks/frameworks.constants'
import { useCustomFrameworkWizard } from 'src/pages/Frameworks/context/CustomFrameworkWizardContext'
import { FlexAlign, FlexColumn } from 'src/components/Reusable/Flex'
import { P } from 'src/components/Reusable/Text/Text.styles'
import { ReactComponent as AddIcon } from 'src/components/Icons/assets/add-icon.svg'
import { ReactComponent as BringIntoIcon } from 'src/components/Icons/assets/bring-into.svg'
import { ReactComponent as CloudUploadIcon } from 'src/components/Icons/assets/cloud-upload.svg'
import { ModalForm, ModalFormId } from '../..'
import { ModalButton, ModalButtonContainer } from '../../ModalForm.styles'

type OptionCard = {
  key: string
  title: string
  description: string
  icon: JSX.Element
  onClick: () => void
}

export const AddComplianceFrameworkModal = (): JSX.Element => {
  const show = useIsShowModal(ModalFormId.ADD_COMPLIANCE_FRAMEWORK)
  const hide = useHideModal({ modalId: ModalFormId.ADD_COMPLIANCE_FRAMEWORK })
  const { openModal } = useModalState()
  const { setIsFromAddFrameworkFlow } = useCustomFrameworkWizard()
  const scanReceptor = useScanReceptor()
  const { data: receptorsData } = useAccountReceptors()

  const grcReceptors = useMemo<ReceptorRecord[]>(
    () =>
      (receptorsData?.getReceptorsList() ?? []).filter(
        (receptor) =>
          receptor.getIsenabled() &&
          GRC_RECEPTOR_MODEL_IDS.has(receptor.getModelid()),
      ),
    [receptorsData],
  )

  const openDownstream = (modalId: ModalFormId) => {
    setIsFromAddFrameworkFlow(true)
    hide()
    openModal(modalId)
  }

  const openGrcImportFor = (receptor: ReceptorRecord) => {
    const receptorId = receptor.getId()
    const modelId = receptor.getModelid()
    if (!receptorId) return
    setGrcWizardReceptorModelId(modelId)
    void scanReceptor({ receptorId, receptorModelId: modelId })
    openDownstream(ModalFormId.GRC_IMPORT_WIZARD)
  }

  const cards: OptionCard[] = [
    {
      key: 'catalog',
      title: 'Add from catalog',
      description:
        'Pick a framework from the Trustero catalog (SOC 2, ISO 27001, and more).',
      icon: <BringIntoIcon />,
      onClick: () => openDownstream(ModalFormId.CHOOSE_COMPLIANCE_FRAMEWORK),
    },
    ...grcReceptors.map<OptionCard>((receptor) => {
      const modelId = receptor.getModelid()
      const displayName = GRC_RECEPTOR_DISPLAY_NAMES[modelId] ?? modelId
      return {
        key: `grc-${modelId}`,
        title: `Add from ${displayName}`,
        description: `Import frameworks, controls, and policies from your connected ${displayName} instance.`,
        icon: <CloudUploadIcon />,
        onClick: () => openGrcImportFor(receptor),
      }
    }),
    {
      key: 'custom',
      title: 'Add custom framework',
      description:
        'Build your own framework with custom requirements and control mappings.',
      icon: <AddIcon />,
      onClick: () =>
        openDownstream(ModalFormId.ADD_CUSTOM_COMPLIANCE_FRAMEWORK),
    },
  ]

  return (
    <ModalForm
      show={show}
      hide={hide}
      formId={ModalFormId.ADD_COMPLIANCE_FRAMEWORK}
      size="xl"
      hideButtons
      title="Add Compliance Framework"
      description="Choose how you'd like to add a compliance framework."
    >
      <form id={ModalFormId.ADD_COMPLIANCE_FRAMEWORK}>
        <ModalButtonContainer>
          {cards.map((card) => (
            <ModalButton key={card.key} onClick={card.onClick}>
              {card.icon}
              <FlexColumn justify={FlexAlign.FLEX_END} align={FlexAlign.CENTER}>
                <p>{card.title}</p>
                <P $isDim>{card.description}</P>
              </FlexColumn>
            </ModalButton>
          ))}
        </ModalButtonContainer>
      </form>
    </ModalForm>
  )
}
